import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { QuickstartController, QuickstartState, QuickstartPhase } from './QuickstartController';

/**
 * React Hook for Quickstart Flow
 * Manages QuickstartController lifecycle and provides state to components
 */
export function useQuickstartFlow(quickstartSlug: string, debugPhase?: QuickstartPhase) {
  const { isLoaded } = useAuth();
  const controllerRef = useRef<QuickstartController | null>(null);
  const [state, setState] = useState<QuickstartState | null>(null);

  useEffect(() => {
    // Wait for Clerk to be loaded before initializing
    if (!isLoaded) {
      console.log('[useQuickstartFlow] Waiting for Clerk to load...');
      return;
    }

    // Create controller
    console.log('[useQuickstartFlow] Clerk loaded, creating controller for:', quickstartSlug, debugPhase ? `(debug: ${debugPhase})` : '');
    const controller = new QuickstartController(quickstartSlug, debugPhase);
    controllerRef.current = controller;

    // Subscribe to controller state
    const listenerId = controller.getState().addListener((newState) => {
      setState(newState);
    });

    // Set initial state
    setState(controller.getState().currentState);

    // Initialize controller
    controller.init();

    // Cleanup on unmount
    return () => {
      console.log('[useQuickstartFlow] Cleaning up controller');
      controller.getState().removeListener(listenerId);
      controller.cleanup();
      controllerRef.current = null;
    };
  }, [quickstartSlug, debugPhase, isLoaded]);

  // Return state and controller methods
  return {
    state,
    controller: controllerRef.current,

    // Expose key controller methods for UI interactions
    onFirstInput: () => controllerRef.current?.onFirstInput(),
    onReturnToPlayNow: () => controllerRef.current?.onReturnToPlayNow(),
    onWishlistClicked: () => controllerRef.current?.onWishlistClicked(),
    onWishlistCTAClicked: () => controllerRef.current?.onWishlistCTAClicked(),
    onWishlistEmailSend: (playerAuthId: string, email: string, persistentId: string) =>
      controllerRef.current?.onWishlistEmailSend(playerAuthId, email, persistentId),
    onDeveloperLogoClicked: (url: string) => controllerRef.current?.onDeveloperLogoClicked(url),
    onThankYouRedirect: (steamUrl: string) => controllerRef.current?.onThankYouRedirect(steamUrl),
    onOpenInBrowserClicked: () => controllerRef.current?.onOpenInBrowserClicked(),
    onAuthSuccess: (playerAuthId: string, email?: string, persistentId?: string) =>
      controllerRef.current?.onAuthSuccess(playerAuthId, email, persistentId),
    onAuthFailure: (reason: string) => controllerRef.current?.onAuthFailure(reason),
    onClerkUnavailable: () => controllerRef.current?.onClerkUnavailable(),
    retryMatchmaking: () => controllerRef.current?.retryMatchmaking(),
    requestBrowserLocks: async () => {
      if (controllerRef.current) {
        await controllerRef.current.requestBrowserLocks();
      }
    },

    // Player event handlers
    onAfkTimeout: () => controllerRef.current?.onAfkTimeout(),
    onAdTimerExpired: () => controllerRef.current?.onAdTimerExpired(),
    onPlayerDisconnect: () => controllerRef.current?.onPlayerDisconnect(true), // true = manual disconnect from UI

    // Timer methods (synchronized with backend)
    getAFKRemainingTime: () => controllerRef.current?.getAFKRemainingTime() ?? 0,
    getAdRemainingTime: () => controllerRef.current?.getAdRemainingTime() ?? 0,

    // Debug methods
    debugJumpToPhase: (phase: QuickstartPhase) => controllerRef.current?.debugJumpToPhase(phase),
    debugSetReleaseTimestamp: (timestamp: number | 'past' | 'future') => controllerRef.current?.debugSetReleaseTimestamp(timestamp),
  };
}

